import { apiSlice } from '../../../store/api/apiSlice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Summary record returned from the list endpoint. */
export interface OnboardingSubmission {
  id: string;
  form_type: string;
  typeform_event_id: string;
  typeform_form_id: string;
  typeform_response_token: string;
  respondent_email: string | null;
  respondent_first_name: string | null;
  respondent_last_name: string | null;
  respondent_company: string | null;
  linked_person_id: string | null;
  status: string;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

/** Full detail including JSONB payload columns. */
export interface OnboardingSubmissionDetail extends OnboardingSubmission {
  answers: Record<string, unknown>[];
  definition?: Record<string, unknown>;
  hidden?: Record<string, string>;
  calculated?: Record<string, unknown>;
  variables?: Record<string, unknown>[];
}

/** Paginated list response. */
export interface ListSubmissionsResponse {
  submissions: OnboardingSubmission[];
  total: number;
  page: number;
  per_page: number;
}

/** Query params for listing submissions. */
export interface ListSubmissionsParams {
  page?: number;
  per?: number;
  form_type?: string;
  status?: string;
  company?: string;
  q?: string;
  sort_by?: string;
  sort_dir?: string;
}

/** Aggregated form summary. */
export interface FormSummary {
  typeform_form_id: string;
  form_type: string;
  form_title?: string;
  submission_count: number;
  has_mappings: boolean;
  unmapped_count: number;
  latest_submission_at?: string;
}

/** A field mapping record. */
export interface FieldMapping {
  id: string;
  typeform_form_id: string;
  field_purpose: string;
  typeform_field_ref: string;
  typeform_field_id?: string;
  field_title?: string;
  created_at: string;
  updated_at: string;
}

/** A field available for mapping (from definition). */
export interface AvailableField {
  id?: string;
  ref?: string;
  title?: string;
  type?: string;
}

/** Response for getFormMappings. */
export interface FormMappingsResponse {
  typeform_form_id: string;
  form_type: string;
  mappings: FieldMapping[];
  available_fields: AvailableField[];
}

/** Input for a single mapping in the set operation. */
export interface SetMappingInput {
  field_purpose: string;
  typeform_field_ref: string;
  typeform_field_id?: string;
  field_title?: string;
}

/** Backfill result. */
export interface BackfillResult {
  total: number;
  updated: number;
  failed: number;
}

/** Reclassify result. */
export interface ReclassifyResult {
  reclassified: number;
  backfilled: number;
  form_type: string;
}

/** Pull result from the Typeform Responses API. */
export interface PullResult {
  fetched: number;
  stored: number;
  duplicates: number;
  failed: number;
}

/** Aggregate result from pulling all configured forms. */
export interface PullAllResult {
  forms: number;
  per_form: Record<string, PullResult>;
  total: PullResult;
}

/** A field group tag record. */
export interface FieldGroupRecord {
  id: string;
  typeform_form_id: string;
  typeform_field_ref: string;
  field_group: string;
  canonical_ref: string | null;
  field_title?: string;
  created_at: string;
  updated_at: string;
}

/** Input for a single group tag in the set operation. */
export interface SetGroupInput {
  typeform_field_ref: string;
  field_group: string;
  canonical_ref?: string | null;
  field_title?: string;
}

/** Response for getFormFieldGroups. */
export interface FormFieldGroupsResponse {
  typeform_form_id: string;
  groups: FieldGroupRecord[];
  canonical_refs: string[];
}

// ---------------------------------------------------------------------------
// API Endpoints
// ---------------------------------------------------------------------------

export const onboardingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Submissions
    listOnboardingSubmissions: builder.query<
      ListSubmissionsResponse,
      ListSubmissionsParams | undefined
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== '') {
              searchParams.set(key, String(value));
            }
          }
        }
        const qs = searchParams.toString();
        return `/admin/onboarding/submissions${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) => [
        'OnboardingSubmissions',
        ...(result?.submissions.map((s) => ({
          type: 'OnboardingSubmission' as const,
          id: s.id,
        })) ?? []),
      ],
    }),

    getOnboardingSubmission: builder.query<
      { submission: OnboardingSubmissionDetail },
      { id: string }
    >({
      query: ({ id }) => `/admin/onboarding/submissions/${id}`,
      providesTags: (_result, _error, { id }) => [{ type: 'OnboardingSubmission', id }],
    }),

    linkSubmission: builder.mutation<{ ok: boolean }, { id: string; person_id: string }>({
      query: ({ id, person_id }) => ({
        url: `/admin/onboarding/submissions/${id}/link`,
        method: 'PATCH',
        body: { person_id },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'OnboardingSubmissions',
        { type: 'OnboardingSubmission', id },
      ],
    }),

    unlinkSubmission: builder.mutation<{ ok: boolean }, { id: string }>({
      query: ({ id }) => ({
        url: `/admin/onboarding/submissions/${id}/link`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'OnboardingSubmissions',
        { type: 'OnboardingSubmission', id },
      ],
    }),

    updateSubmissionStatus: builder.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/admin/onboarding/submissions/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'OnboardingSubmissions',
        { type: 'OnboardingSubmission', id },
      ],
    }),

    // Aggregates
    listDistinctCompanies: builder.query<{ companies: string[] }, void>({
      query: () => '/admin/onboarding/companies',
      providesTags: ['OnboardingSubmissions'], // Invalidate when submissions change
    }),

    // Forms
    listOnboardingForms: builder.query<{ forms: FormSummary[] }, void>({
      query: () => '/admin/onboarding/forms',
      providesTags: ['OnboardingForms'],
    }),

    getFormMappings: builder.query<FormMappingsResponse, { formId: string }>({
      query: ({ formId }) => `/admin/onboarding/forms/${encodeURIComponent(formId)}/mappings`,
      providesTags: (_result, _error, { formId }) => [{ type: 'FormMappings', id: formId }],
    }),

    setFormMappings: builder.mutation<
      { mappings: FieldMapping[] },
      { formId: string; mappings: SetMappingInput[] }
    >({
      query: ({ formId, mappings }) => ({
        url: `/admin/onboarding/forms/${encodeURIComponent(formId)}/mappings`,
        method: 'PUT',
        body: { mappings },
      }),
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'FormMappings', id: formId },
        'OnboardingForms',
        'OnboardingSubmissions',
      ],
    }),

    backfillIdentity: builder.mutation<BackfillResult, { formId: string }>({
      query: ({ formId }) => ({
        url: `/admin/onboarding/forms/${encodeURIComponent(formId)}/backfill`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { formId }) => [
        'OnboardingSubmissions',
        { type: 'FormMappings', id: formId },
        'OnboardingForms',
      ],
    }),

    reclassifyFormType: builder.mutation<ReclassifyResult, { formId: string; form_type: string }>({
      query: ({ formId, form_type }) => ({
        url: `/admin/onboarding/forms/${encodeURIComponent(formId)}/reclassify`,
        method: 'POST',
        body: { form_type },
      }),
      invalidatesTags: ['OnboardingForms', 'OnboardingSubmissions'],
    }),

    pullResponses: builder.mutation<PullResult, { formId: string }>({
      query: ({ formId }) => ({
        url: `/admin/onboarding/forms/${encodeURIComponent(formId)}/pull`,
        method: 'POST',
      }),
      invalidatesTags: ['OnboardingForms', 'OnboardingSubmissions'],
    }),

    pullAllResponses: builder.mutation<PullAllResult, void>({
      query: () => ({
        url: '/admin/onboarding/pull-all',
        method: 'POST',
      }),
      invalidatesTags: ['OnboardingForms', 'OnboardingSubmissions'],
    }),

    backfillAllIdentity: builder.mutation<BackfillResult, void>({
      query: () => ({
        url: '/admin/onboarding/backfill-all',
        method: 'POST',
      }),
      invalidatesTags: ['OnboardingForms', 'OnboardingSubmissions'],
    }),

    // Field Groups
    getFormFieldGroups: builder.query<FormFieldGroupsResponse, { formId: string }>({
      query: ({ formId }) => `/admin/onboarding/forms/${encodeURIComponent(formId)}/groups`,
      providesTags: (_result, _error, { formId }) => [
        { type: 'FormMappings', id: `groups-${formId}` },
      ],
    }),

    setFormFieldGroups: builder.mutation<
      { groups: FieldGroupRecord[] },
      { formId: string; groups: SetGroupInput[] }
    >({
      query: ({ formId, groups }) => ({
        url: `/admin/onboarding/forms/${encodeURIComponent(formId)}/groups`,
        method: 'PUT',
        body: { groups },
      }),
      invalidatesTags: (_result, _error, { formId }) => [
        { type: 'FormMappings', id: `groups-${formId}` },
      ],
    }),
  }),
});

// ---------------------------------------------------------------------------
// Exported Hooks
// ---------------------------------------------------------------------------

export const {
  useListOnboardingSubmissionsQuery,
  useGetOnboardingSubmissionQuery,
  useLinkSubmissionMutation,
  useUnlinkSubmissionMutation,
  useUpdateSubmissionStatusMutation,
  useListDistinctCompaniesQuery,
  useListOnboardingFormsQuery,
  useGetFormMappingsQuery,
  useSetFormMappingsMutation,
  useBackfillIdentityMutation,
  useReclassifyFormTypeMutation,
  usePullResponsesMutation,
  usePullAllResponsesMutation,
  useBackfillAllIdentityMutation,
  useGetFormFieldGroupsQuery,
  useSetFormFieldGroupsMutation,
} = onboardingApi;
